import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ChevronRightIcon,
  InformationCircleIcon,
  XMarkIcon,
  UserGroupIcon,
  UserPlusIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "motion/react";
import {
  formatDate,
  formatDateWithDay,
  formatTime,
} from "../../../../../../lib/utils";
import {
  ActivityFragment,
  CreateDocumentGroupSharingDocument,
  CreateDocumentSharingDocument,
  DeleteDocumentGroupSharingDocument,
  DeleteDocumentSharingDocument,
  DocumentFragment,
  DocumentsByUserOrganizationIdDocument,
  EventDetailsDocument,
  OrganizationDocumentsDocument,
  SharingMode,
  UpdateDocumentDetailsDocument,
  UserGroupFragment,
} from "../../../../../../__generated__/graphql";
import DocumentPreview from "../../../../../../components/DocumentPreview";
import FormInput from "../../../../../../components/FormInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUser } from "../../../../../../contexts/UserContext/hooks/useUser";
import useOrganizationSharings from "../../../../../../hooks/useOrganizationSharings";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";
import Button from "../../../../../../components/Button";
import ParticipantsSelectionModal from "../../../../../../components/ParticipantsSelectionModal";
import useActivityById from "../../../../../projects/pages/project/components/Schedule/hooks/useActivityById";
import { ActivityDetailsModal } from "../../../../../../components/ActivityDetailsModal";
import useNav from "../../../../../../hooks/useNavigation";
import useEventDetails from "../../../../../events/pages/event/hooks/useEventDetails";
import Checkbox from "../../../../../../components/Checkbox";
import useProjectDetails from "../../../../../projects/pages/project/hooks/useProjectDetails";
import useManageParticipants, {
  ReasonForSharing,
} from "../../../../../../hooks/useManageParticipants";
import TextParser from "../../../../../../components/TextParser";
import { PublicUserOrganization } from "../../../../../../__generated__/graphql";
import DocumentProperties from "../DocumentProperties";
import ResourceViewsComponent from "../../../../../../components/ResourceViewsComponent";
import UserGroupModal from "../../../../../projects/pages/project/components/ProjectParticipants/components/UserGroupModal";

interface ParticipantOption {
  value: string;
  organizationId: string;
  userOrganizationId: string;
  label: string;
}

const documentModalSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  participants: z
    .array(
      z.object({
        value: z.string(),
        organizationId: z.string(),
        userOrganizationId: z.string(),
        label: z.string(),
      })
    )
    .optional(),
  isPublic: z.boolean(),
});

type DocumentModalSchema = z.infer<typeof documentModalSchema>;

export default function DocumentDetailsModal({
  isOpen,
  onClose,
  onInvitationModalOpen,
  document,
}: {
  isOpen: boolean;
  onClose: () => void;
  onInvitationModalOpen: () => void;
  document: DocumentFragment;
}) {
  const { currentOrganization } = useUser();
  const navigateWithLog = useNav();

  const [displayActivityDetails, setDisplayActivityDetails] = useState(false);

  const [createDocumentSharing, { loading: createDocumentSharingLoading }] =
    useMutation(CreateDocumentSharingDocument);
  const [deleteDocumentSharing, { loading: deleteDocumentSharingLoading }] =
    useMutation(DeleteDocumentSharingDocument);
  const [createDocumentGroupSharing] = useMutation(CreateDocumentGroupSharingDocument);
  const [deleteDocumentGroupSharing] = useMutation(DeleteDocumentGroupSharingDocument);
  const [updateDocument, { loading: updateDocumentLoading }] = useMutation(
    UpdateDocumentDetailsDocument
  );
  const [isSelectingParticipants, setIsSelectingParticipants] = useState(false);
  const [selectedGroupForModal, setSelectedGroupForModal] =
    useState<UserGroupFragment | null>(null);
  const { data: activityByIdData } = useActivityById(
    document.activityId ?? undefined
  );
  const { data: eventDetailsData } = useEventDetails(
    document.eventId ?? undefined
  );
  const { data: projectDetailsData } = useProjectDetails(
    document.projectId ?? undefined
  );

  // Derive effective projectId: direct projectId, OR from activity's project, OR from event's project
  const effectiveProjectId = useMemo(() => {
    if (document.projectId) return document.projectId;
    if (activityByIdData?.activity?.projectId) return activityByIdData.activity.projectId;
    if (activityByIdData?.activity?.eventId && eventDetailsData?.event?.projectId) {
      return eventDetailsData.event.projectId;
    }
    if (eventDetailsData?.event?.projectId) return eventDetailsData.event.projectId;
    return null;
  }, [
    document.projectId,
    activityByIdData?.activity?.projectId,
    activityByIdData?.activity?.eventId,
    eventDetailsData?.event?.projectId,
  ]);

  const participantsFor: ReasonForSharing = useMemo(() => {
    if (document.activityId) {
      return "activity_document";
    }

    if (document.eventId) {
      return "event_document";
    }

    if (document.projectId) {
      return "project_document";
    }

    return "document";
  }, [document.activityId, document.eventId, document.projectId]);

  const { possibleParticipants, alreadyAddedParticipants } =
    useManageParticipants({
      eventId: document.eventId ?? undefined,
      documentId: document.id ?? undefined,
      projectId: document.projectId ?? undefined,
      activityId: document.activityId ?? undefined,
      participantsFor,
    });

  const {
    loading: organizationSharingsLoading,
    error: organizationSharingsError,
    externalUserOrganizations,
  } = useOrganizationSharings();

  const participantWording = useMemo(() => {
    if (activityByIdData?.activity) {
      return "Sélectionnez parmi les participants de l'activité";
    }

    if (document.eventId) {
      return "Sélectionnez parmi les participants de l'évènement";
    }

    if (document.projectId) {
      return "Sélectionnez parmi les participants du projet";
    }
  }, [activityByIdData?.activity, document.eventId, document.projectId]);

  const participantOptions = useMemo(() => {
    const options: {
      value: string;
      userOrganizationId: string;
      label: string;
      organizationId: string;
    }[] = [];

    possibleParticipants.forEach((participant) => {
      options.push({
        value: participant.id,
        userOrganizationId: participant.id,
        label: participant.name,
        organizationId: participant.organizationId,
      });
    });

    return options;
  }, [possibleParticipants]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DocumentModalSchema>({
    resolver: zodResolver(documentModalSchema),
    defaultValues: {
      title: document.title,
      description: document.description || "",
      participants: alreadyAddedParticipants.map((participant) => ({
        value: participant.id,
        organizationId: participant.organizationId,
        userOrganizationId: participant.id,
        label: participant.name,
      })),
      isPublic: document.isPublic,
    },
  });

  useEffect(() => {
    reset({
      title: document.title,
      description: document.description || "",
      participants: alreadyAddedParticipants.map((participant) => ({
        value: participant.id,
        organizationId: participant.organizationId,
        userOrganizationId: participant.id,
        label: participant.name,
      })),
      isPublic: document.isPublic,
    });
  }, [alreadyAddedParticipants, document, reset]);

  const isOwner = document.ownerOrganizationId === currentOrganization?.id;
  const isDocumentSent =
    document.originalSenderOrganizationId !== null ||
    document.originalSenderUserOrganization !== null;

  const participants = watch("participants");
  const participantsHaveChanged =
    participants
      ?.map((p) => p.userOrganizationId)
      .sort()
      .join(",") !==
      alreadyAddedParticipants
        .map((participant) => participant.id)
        .sort()
        .join(",") || watch("isPublic") !== document.isPublic;

  // Get currently selected users from document sharings
  const currentlySharedUsers: PublicUserOrganization[] = useMemo(() => {
    return (document.documentSharings ?? [])
      .filter((ds) => ds?.recipientPublicUserOrganization)
      .map((ds) => ds!.recipientPublicUserOrganization!);
  }, [document.documentSharings]);

  // Get currently shared groups
  const currentlySharedGroups: UserGroupFragment[] = useMemo(() => {
    return (document.documentGroupSharings ?? []).map((gs) => gs.userGroup);
  }, [document.documentGroupSharings]);

  const findDocumentSharingByUserOrganizationId = (
    userOrganizationId: string
  ) => {
    return document.documentSharings?.find(
      (documentSharing) =>
        documentSharing?.recipientUserOrganizationId === userOrganizationId
    );
  };

  const findUserByUserOrganizationId = (userOrganizationId: string) => {
    return participantOptions.find(
      (option) => option.userOrganizationId === userOrganizationId
    );
  };

  const handleClose = () => {
    reset({
      title: document.title,
      description: document.description || "",
      participants: alreadyAddedParticipants.map((participant) => ({
        value: participant.id,
        organizationId: participant.organizationId,
        userOrganizationId: participant.id,
        label: participant.name,
      })),
      isPublic: document.isPublic,
    });
    onClose();
  };

  const getRefetchQueries = (recipientIds: string[]) => [
    ...(document.eventId
      ? [
          {
            query: EventDetailsDocument,
            variables: {
              eventId: document.eventId,
            },
          },
        ]
      : []),
    {
      query: OrganizationDocumentsDocument,
    },
    ...recipientIds.map((recipientId) => ({
      query: DocumentsByUserOrganizationIdDocument,
      variables: {
        userOrganizationId: recipientId,
      },
    })),
  ];

  const handleResourceSharingDeletion = async (recipientIds: string[]) => {
    if (recipientIds.length === 0) return;

    await Promise.all(
      recipientIds.map(async (recipientId) => {
        const documentSharing =
          findDocumentSharingByUserOrganizationId(recipientId);
        if (!documentSharing?.id) return null;

        return deleteDocumentSharing({
          variables: {
            documentSharingId: documentSharing.id,
          },
          refetchQueries: getRefetchQueries(recipientIds),
        });
      })
    );
  };

  const handleResourceSharingCreation = async (recipientIds: string[]) => {
    if (recipientIds.length === 0) return;

    await Promise.all(
      recipientIds.map(async (recipientId) => {
        const documentSharing =
          findDocumentSharingByUserOrganizationId(recipientId);
        if (documentSharing?.id) return null;

        const user = findUserByUserOrganizationId(recipientId);
        if (!user) return null;

        return createDocumentSharing({
          variables: {
            createDocumentSharingInput: {
              documentId: document.id,
              recipientOrganizationId: user.organizationId,
              recipientUserOrganizationId: recipientId,
              sharingMode: SharingMode.Shared,
            },
          },
          refetchQueries: getRefetchQueries(recipientIds),
        });
      })
    );
  };

  const updateResourceSharings = async (
    newParticipants: ParticipantOption[]
  ) => {
    const newParticipantIds = newParticipants.map((p) => p.userOrganizationId);
    const recipientsToDelete = alreadyAddedParticipants.filter(
      (participant) => !newParticipantIds.includes(participant.id)
    );
    const recipientsToCreate = newParticipantIds.filter(
      (id) =>
        !alreadyAddedParticipants
          .map((participant) => participant.id)
          .includes(id)
    );

    await handleResourceSharingDeletion(
      recipientsToDelete.map((participant) => participant.id)
    );
    await handleResourceSharingCreation(recipientsToCreate);
  };

  const handleParticipantsAndGroupsSelected = async (
    selectedUsers: PublicUserOrganization[],
    selectedGroups?: UserGroupFragment[]
  ) => {
    try {
      // Handle individual user sharings
      const currentParticipantIds = new Set(
        (document.documentSharings ?? []).map(
          (ds) => ds?.recipientUserOrganizationId
        )
      );
      const newParticipantIds = new Set(selectedUsers.map((u) => u.id));

      // Users to add
      const usersToAdd = selectedUsers.filter(
        (u) => !currentParticipantIds.has(u.id)
      );
      // Users to remove
      const sharingsToRemove = (document.documentSharings ?? []).filter(
        (ds) => ds?.recipientUserOrganizationId && !newParticipantIds.has(ds.recipientUserOrganizationId)
      );

      // Delete sharings for removed users
      for (const sharing of sharingsToRemove) {
        if (sharing?.id) {
          await deleteDocumentSharing({
            variables: { documentSharingId: sharing.id },
            refetchQueries: getRefetchQueries([]),
          });
        }
      }

      // Create sharings for new users
      for (const user of usersToAdd) {
        await createDocumentSharing({
          variables: {
            createDocumentSharingInput: {
              documentId: document.id,
              recipientOrganizationId: user.organizationId,
              recipientUserOrganizationId: user.id,
              sharingMode: SharingMode.Shared,
            },
          },
          refetchQueries: getRefetchQueries([]),
        });
      }

      // Handle group sharings
      if (selectedGroups) {
        const currentGroupIds = new Set(
          (document.documentGroupSharings ?? []).map((gs) => gs.userGroupId)
        );
        const newGroupIds = new Set(selectedGroups.map((g) => g.id));

        // Groups to add
        const groupsToAdd = selectedGroups.filter(
          (g) => !currentGroupIds.has(g.id)
        );
        // Groups to remove
        const groupSharingsToRemove = (document.documentGroupSharings ?? []).filter(
          (gs) => !newGroupIds.has(gs.userGroupId)
        );

        // Delete group sharings for removed groups
        for (const groupSharing of groupSharingsToRemove) {
          await deleteDocumentGroupSharing({
            variables: { documentGroupSharingId: groupSharing.id },
            refetchQueries: getRefetchQueries([]),
          });
        }

        // Create group sharings for new groups
        for (const group of groupsToAdd) {
          await createDocumentGroupSharing({
            variables: {
              createDocumentGroupSharingInput: {
                documentId: document.id,
                userGroupId: group.id,
              },
            },
            refetchQueries: getRefetchQueries([]),
          });
        }
      }

      // Update form state to reflect changes
      setValue(
        "participants",
        selectedUsers.map((u) => ({
          value: u.id,
          organizationId: u.organizationId,
          userOrganizationId: u.id,
          label: u.name,
        }))
      );

      toast.success("Partages mis à jour");
      setIsSelectingParticipants(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la mise à jour des partages"
      );
    }
  };

  const onSubmit = async (data: DocumentModalSchema) => {
    try {
      await updateDocument({
        variables: {
          updateDocumentInput: {
            id: document.id,
            title: data.title,
            description: data.description,
            isPublic: data.isPublic,
          },
        },
      });
      if (data.participants) {
        await updateResourceSharings(
          data.participants.map((p) => ({
            value: p.userOrganizationId,
            organizationId: p.organizationId,
            userOrganizationId: p.userOrganizationId,
            label: p.label,
          }))
        );
      }

      toast.success("Document mis à jour avec succès");
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la mise à jour du document"
      );
    }
  };

  if (organizationSharingsLoading || organizationSharingsError) {
    return null;
  }

  const documentDetailsForm = () => {
    if (!isOwner || isDocumentSent) {
      return document.description ? (
        <div className="flex flex-col gap-4">
          <div className="font-bold text-lg">Description</div>
          <div className="overflow-y-auto pr-1 custom-scrollbar text-sm">
            <TextParser text={document.description} />
          </div>
        </div>
      ) : null;
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
        <div className="font-bold text-md">Informations du document</div>

        <div className="flex flex-col gap-2">
          <FormInput
            id="title"
            label="Titre"
            register={register}
            error={errors.title}
          />

          <div>
            <label className="block text-sm font-medium">Description</label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full p-2 text-xs rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
            />
          </div>

          <Button
            type="submit"
            isLoading={
              updateDocumentLoading ||
              createDocumentSharingLoading ||
              deleteDocumentSharingLoading
            }
            className="w-full"
            disabled={!isDirty && !participantsHaveChanged}
          >
            Enregistrer
          </Button>
        </div>
      </form>
    );
  };

  const activityDetailsButton = (activity: ActivityFragment) => {
    return (
      <div className="flex flex-col gap-1">
        <div className="font-bold text-md">Pour cette activité</div>

        <div
          className="flex justify-between items-center gap-1 bg-card border border-border rounded-md p-2 cursor-pointer hover:bg-card/50 transition-all duration-300"
          onClick={() => {
            setDisplayActivityDetails(true);
            handleClose();
          }}
        >
          <div className="flex flex-col">
            <div className="font-bold text-sm text-primary">
              {activity.title}
            </div>
            <div className="text-sm text-secondary">
              {formatDateWithDay(activity.startDate)} à{" "}
              {formatTime(activity.startDate)}
            </div>
          </div>

          <div>
            <ChevronRightIcon className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  };

  const eventDetailsButton = ({
    eventId,
    eventName,
  }: {
    eventId: string;
    eventName: string;
  }) => {
    return (
      <div className="flex flex-col gap-1">
        <div className="font-bold text-md">Pour cet évènement</div>

        <div
          className="flex justify-between items-center gap-1 bg-card border border-border rounded-md p-2 cursor-pointer hover:bg-card/50 transition-all duration-300"
          onClick={() => {
            navigateWithLog(`/events/${eventId}`);
            handleClose();
          }}
        >
          <div className="flex flex-col">
            <div className="font-bold text-sm text-primary">{eventName}</div>
          </div>

          <div>
            <ChevronRightIcon className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  };

  const projectDetailsButton = ({
    projectId,
    projectName,
  }: {
    projectId: string;
    projectName: string;
  }) => {
    return (
      <div className="flex flex-col gap-1">
        <div className="font-bold text-md">Pour ce projet</div>

        <div
          className="flex justify-between items-center gap-1 bg-card border border-border rounded-md p-2 cursor-pointer hover:bg-card/50 transition-all duration-300"
          onClick={() => {
            handleClose();
            navigateWithLog(`/projects/${projectId}`);
          }}
        >
          <div className="font-bold text-sm text-primary">{projectName}</div>

          <div>
            <ChevronRightIcon className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  };

  const sharingSection = () => {
    const hasGroupSharings = document.documentGroupSharings && document.documentGroupSharings.length > 0;
    const hasIndividualSharings = document.documentSharings && document.documentSharings.length > 0;
    const showGroupSelection = effectiveProjectId != null;

    return (
      <div className="flex flex-col">
        <div className="font-bold text-md w-md">Partager</div>

        {participantWording ? (
          <div className="text-sm text-secondary">{participantWording}</div>
        ) : null}

        {/* Add participants/groups button */}
        {!watch("isPublic") && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-3"
          >
            <button
              data-testid="document-add-sharing-button"
              type="button"
              onClick={() => setIsSelectingParticipants(true)}
              className="flex items-center gap-2 px-3 py-2 w-full border border-dashed border-border rounded-lg text-sm text-secondary hover:text-primary hover:border-accent transition-all duration-200"
            >
              <UserPlusIcon className="w-4 h-4" />
              <span>
                {showGroupSelection
                  ? "Ajouter des participants ou des groupes"
                  : "Ajouter des participants"}
              </span>
            </button>
          </motion.div>
        )}

        {/* Display group sharings */}
        {hasGroupSharings && !watch("isPublic") && (
          <div className="mt-3">
            <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
              Groupes ({document.documentGroupSharings?.length})
            </div>
            <div className="flex flex-col gap-2">
              {document.documentGroupSharings?.map((groupSharing) => (
                <div
                  key={groupSharing.id}
                  className="flex items-center gap-2 p-2 bg-card border border-border rounded-lg group"
                >
                  <button
                    type="button"
                    onClick={() => setSelectedGroupForModal(groupSharing.userGroup)}
                    className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                      <UserGroupIcon className="w-4 h-4 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="font-medium text-sm text-primary truncate">
                        {groupSharing.userGroup.name}
                      </div>
                      <div className="text-xs text-secondary">
                        {groupSharing.userGroup.members?.length ?? 0} membre
                        {(groupSharing.userGroup.members?.length ?? 0) > 1 ? "s" : ""}
                      </div>
                    </div>
                  </button>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await deleteDocumentGroupSharing({
                            variables: { documentGroupSharingId: groupSharing.id },
                            refetchQueries: getRefetchQueries([]),
                          });
                          toast.success("Groupe retiré");
                        } catch {
                          toast.error("Erreur lors de la suppression");
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/10 rounded transition-all"
                    >
                      <XCircleIcon className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display individual sharings */}
        {hasIndividualSharings && !watch("isPublic") && (
          <div className="mt-3">
            <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
              Personnes ({document.documentSharings?.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {document.documentSharings?.map((sharing) => (
                sharing?.recipientPublicUserOrganization && (
                  <div
                    key={sharing.id}
                    className="flex items-center gap-1.5 px-2 py-1 bg-card border border-border rounded-full text-sm group"
                  >
                    <span className="text-primary">
                      {sharing.recipientPublicUserOrganization.name}
                    </span>
                    {isOwner && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await deleteDocumentSharing({
                              variables: { documentSharingId: sharing.id },
                              refetchQueries: getRefetchQueries([]),
                            });
                            toast.success("Partage retiré");
                          } catch {
                            toast.error("Erreur lors de la suppression");
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-all"
                      >
                        <XCircleIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {document.activityId || document.eventId || document.projectId ? (
          <div
            data-testid="document-public-checkbox"
            className="flex items-center gap-2 my-4 cursor-pointer font-bold"
            onClick={() => {
              setValue("isPublic", !watch("isPublic"));
            }}
          >
            <Checkbox checked={watch("isPublic")} onChange={() => {}} />
            <div className="text-sm text-secondary hover:text-primary transition-all duration-300">
              Partager avec tous les participants
            </div>
          </div>
        ) : null}

        {!watch("isPublic") && !hasIndividualSharings && !hasGroupSharings ? (
          <div className="text-sm text-accent flex items-center gap-1 mt-4">
            <InformationCircleIcon className="w-4 h-4" />
            Aucun partage. Cliquez sur le bouton ci-dessus pour partager.
          </div>
        ) : null}

        {/* ParticipantsSelectionModal */}
        <ParticipantsSelectionModal
          isOpen={isSelectingParticipants}
          onClose={() => setIsSelectingParticipants(false)}
          onSubmit={handleParticipantsAndGroupsSelected}
          title={showGroupSelection ? "Partager avec" : "Sélectionner des participants"}
          description={
            showGroupSelection
              ? "Sélectionnez des participants ou des groupes pour partager ce document."
              : "Sélectionnez des participants pour partager ce document."
          }
          initialSelectedUsers={currentlySharedUsers}
          initialSelectedGroups={currentlySharedGroups}
          showGroups={showGroupSelection}
          projectId={effectiveProjectId ?? undefined}
          submitButtonText="Confirmer"
        />
      </div>
    );
  };

  const linkedToRessourceButton = () => {
    if (!document.activityId && !document.eventId && !document.projectId)
      return null;

    if (activityByIdData?.activity) {
      return activityDetailsButton(activityByIdData.activity);
    }

    if (eventDetailsData?.event) {
      return eventDetailsButton({
        eventId: eventDetailsData.event.id,
        eventName: eventDetailsData.event.name,
      });
    }

    if (projectDetailsData?.project) {
      return projectDetailsButton({
        projectId: projectDetailsData.project.id,
        projectName: projectDetailsData.project.name,
      });
    }
  };

  const documentSubtitle = () => {
    if (isDocumentSent) {
      return `Envoyé par ${document.originalSenderUserOrganization?.name}`;
    } else {
      return `Créé le ${formatDate(document.createdAt)} par ${
        document.ownerUserOrganization?.name
      }`;
    }
  };

  // User can edit if they're in the same organization that created the group
  const canEditGroup = (group: UserGroupFragment) =>
    currentOrganization?.id === group.organizationId;

  return (
    <>
      <UserGroupModal
        isOpen={!!selectedGroupForModal}
        onClose={() => setSelectedGroupForModal(null)}
        userGroup={selectedGroupForModal}
        readOnly={selectedGroupForModal ? !canEditGroup(selectedGroupForModal) : true}
        projectId={selectedGroupForModal?.projectId ?? document.projectId ?? undefined}
        eventId={selectedGroupForModal?.eventId ?? document.eventId ?? undefined}
      />

      {activityByIdData?.activity ? (
        <ActivityDetailsModal
          activityId={activityByIdData.activity.id}
          eventId={activityByIdData.activity.eventId ?? null}
          isOpen={displayActivityDetails}
          onClose={() => setDisplayActivityDetails(false)}
        />
      ) : null}

      <Dialog open={isOpen} onClose={handleClose} className="relative z-100">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 flex w-screen items-center justify-center p-4"
        >
          <DialogPanel data-testid="document-details-modal" className="w-5xl bg-dark border border-input p-8 rounded-lg text-primary max-h-[100vh] overflow-y-auto custom-scrollbar">
            <DialogTitle className="font-bold text-xl flex items-center justify-between">
              {document.title}

              <XMarkIcon
                className="w-4 h-4 cursor-pointer"
                onClick={handleClose}
              />
            </DialogTitle>
            <div className="text-sm text-secondary mt-1">
              {documentSubtitle()}
            </div>
            <ResourceViewsComponent
              resourceViews={document.resourceViews ?? []}
              isRessourcePublic={document.isPublic}
              participants={
                document.documentSharings
                  ?.filter(
                    (sharing) => !!sharing?.recipientPublicUserOrganization
                  )
                  .map((sharing) => sharing?.recipientPublicUserOrganization) ??
                []
              }
            />

            <div className="flex gap-4 mt-4 overflow-x-hidden">
              <div className="flex flex-col gap-4">
                <DocumentPreview
                  documentToPreview={document}
                  onDelete={handleClose}
                />
                <DocumentProperties document={document} />
              </div>

              <div className="flex flex-col gap-4 w-xs">
                {linkedToRessourceButton()}

                {isOwner && !isDocumentSent ? (
                  <div className="flex flex-col gap-4">
                    {externalUserOrganizations?.length === 0 ? (
                      <div>
                        <div className="font-bold text-md w-md">Inviter</div>
                        <div className="flex flex-col gap-4">
                          <p className="text-sm text-gray-500">
                            Invitez des personnes pour pouvoir partager ce
                            document
                          </p>
                          <Button
                            onClick={() => {
                              handleClose();
                              onInvitationModalOpen();
                            }}
                          >
                            Inviter des personnes
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {sharingSection()}
                  </div>
                ) : null}

                {documentDetailsForm()}
              </div>
            </div>
          </DialogPanel>
        </motion.div>
      </Dialog>
    </>
  );
}
